import * as React from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  CircularProgress,
  Paper,
  Grid,
  TextField
} from '@mui/material';
import { Autocomplete } from '@mui/material';

import api from "../../services/api";
import { UF_TODOS } from '../../config';
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { pt } from 'yup-locale-pt';
import {
  TextField as CustomTextField,
  Select,
  Checkbox,
  SelectUF,
} from '../../components';
import { useStoreState, useStoreActions } from 'easy-peasy';
import HelpDialog from './HelpDialog.js';
import DialogoConfirmacaoSimples from '../../components/DialogoConfirmacaoSimples';

yup.setLocale(pt);


const InstFinSelect = ({ useFormRef, schema }) => {
  const [listaInstFinanceiras, setListaInstFinanceiras] = React.useState([]);
  let startLoading = useStoreActions((actions) => actions.nav.startLoading);
  let stopLoading = useStoreActions((actions) => actions.nav.stopLoading);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(async () => {
    startLoading();
    try {
      setListaInstFinanceiras((await api.http.get('/inst-financeiras')).data);
      stopLoading();
      setMounted(true);
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
    }
  }, []);

  return (
    <FormControl sx={{ my: 2 }} fullWidth>
      {mounted &&
        <Select
          required
          field="instFinanceiraId"
          useFormRef={useFormRef}
          schema={schema}
          options={listaInstFinanceiras}
          getOptionLabel={option => option.nome}
        />
      }
    </FormControl>
  );
};

const DialogoUsuario = ({ open, onClose, roles, usuario }) => {
  let loading = useStoreState((state) => state.nav.loading);
  let startLoading = useStoreActions((actions) => actions.nav.startLoading);
  let stopLoading = useStoreActions((actions) => actions.nav.stopLoading);
  const [states, setStates] = React.useState([]);
  const [confirmarReenvio, setConfirmarReenvio] = React.useState(false);
  const authUser = useStoreState((state) => state.auth.user);

  const defaultValues = {
    empresa: '',
    nome: '',
    email: '',
    roleId: '',
    habilitado: true,
    programaIds: []
  };
  const schema = yup.object({
    empresa: yup.string().label('Empresa').required(),
    nome: yup.string().label('Nome').required(),
    email: yup.string().email().label('Email').required(),
    roleId: yup.string().label('Função').required(),
    instFinanceiraId: yup.number().positive().nullable()
      .label('Instituição Financeira')
      .when('roleId', {
        is: 'instFin',
        then: (schema) => schema.required()
      }),
    uf: yup.string().label("Estado").required(),
    habilitado: yup.boolean().label('Habilitado').required(),
    programaIds: yup.array().of(yup.number()).label('Programas').nullable()
  }).required();
  const useFormRef = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues: defaultValues
  });

  const { handleSubmit, reset, watch,
    formState: { isDirty, isSubmitting } } = useFormRef;

  const roleId = watch('roleId');

  const onSave = async (data) => {
    startLoading();
    try {
      if (data.id) {
        await api.http.put('/users/' + data.id, data);
      } else {
        await api.http.post('/users', data);
      }
      stopLoading({ severity: 'success' });
      close(true);
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
      throw error;
    }
  };

  const close = (reload = false) => {
    setConfirmarReenvio(false);
    reset(defaultValues);
    onClose(reload);
  };

  const reenviarBoasVindas = async () => {
    setConfirmarReenvio(false);
    startLoading();
    try {
      const { data } = await api.http.post(`/users/${usuario.id}/reenviar-boas-vindas`);
      stopLoading({
        message: data.message,
        severity: 'success'
      });
    } catch (error) {
      stopLoading({
        message: error.response?.data?.message || error.toString(),
        severity: 'error'
      });
    }
  };

  React.useEffect(() => {
    if (usuario) {
      reset({
        ...usuario,
        programaIds: usuario.programas?.map(p => p.id) || []
      });
    }
  }, [usuario]);

  React.useEffect(() => {
    ///recuperar dados da api ibge e preencher o dropdow estados
    async function fetchStates() {
      try {
        const response = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados');
        const data = await response.json();

        const sortedStates = data.sort((a, b) => {
          return a.nome.localeCompare(b.nome); // Comparando os nomes dos estados
        });

        // "Todos os estados" só existe aqui (cadastro de usuário, só superAdmin) - as telas
        // públicas montam a própria lista do IBGE e não oferecem essa opção
        setStates([...sortedStates, { sigla: UF_TODOS, nome: "Todos os estados" }]);

      } catch (error) {
        console.error('Error fetching states:', error);
      }
    }

    fetchStates();
  }, [usuario]);

  const [listaProgramas, setListaProgramas] = React.useState([]);

  React.useEffect(() => {
    const fetchProgramas = async () => {
      try {
        const res = await api.http.get('/programas');
        setListaProgramas(res.data);
      } catch (err) {
        console.error('Erro ao carregar programas:', err);
      }
    };

    fetchProgramas();
  }, []);

  return (
    <Dialog open={open} onClose={() => close()}>
      <DialogTitle>Dados do usuário</DialogTitle>
      <DialogContent>
        <FormControl sx={{ my: 2 }} fullWidth>
          <CustomTextField
            margin="dense"
            fullWidth
            field="empresa"
            useFormRef={useFormRef} schema={schema} />
        </FormControl>
        <FormControl sx={{ my: 2 }} fullWidth>
          <CustomTextField
            margin="dense"
            fullWidth
            field="nome"
            useFormRef={useFormRef} schema={schema} />
        </FormControl>
        <FormControl sx={{ my: 2 }} fullWidth>
          <CustomTextField
            margin="dense"
            fullWidth
            field="email"
            type="email"
            useFormRef={useFormRef} schema={schema} />
        </FormControl>
        <FormControl sx={{ my: 2 }} fullWidth>
          <SelectUF
            field="uf"
            useFormRef={useFormRef}
            schema={schema}
            //disabled={disabled}
            options={states}
            getOptionLabel={(option) => option.nome}
          />
        </FormControl>
        <Grid container direction="row"
          justifyContent="center"
          alignItems="center">
          <Grid item xs={10}>
            <FormControl sx={{ my: 2 }} fullWidth>
              <Select
                field="roleId"
                useFormRef={useFormRef}
                schema={schema}
                options={roles}
                getOptionLabel={option => option.name}
              />
            </FormControl>
          </Grid>
          <Grid item xs={2} sx={{ textAlign: 'center' }}>
            <HelpDialog />

          </Grid>
        </Grid>
        <FormControl sx={{ my: 2 }} fullWidth>
          <Controller
            name="programaIds"
            control={useFormRef?.control}
            render={({ field }) => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={listaProgramas}
                getOptionLabel={(option) => option.nome}
                value={listaProgramas.filter((opt) =>
                  (field.value || []).includes(opt.id)
                )}
                onChange={(_, newValue) =>
                  field.onChange(newValue.map((opt) => opt.id))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Programas"
                    error={!!useFormRef?.formState?.errors?.programaIds}
                    helperText={useFormRef?.formState?.errors?.programaIds?.message}
                  />
                )}
              />
            )}
          />
        </FormControl>
        {roleId == 'instFin' &&
          <InstFinSelect useFormRef={useFormRef} schema={schema} />
        }
        <FormControl sx={{ my: 2 }} fullWidth>
          <Checkbox field="habilitado"
            useFormRef={useFormRef} schema={schema} />
        </FormControl>
      </DialogContent>
      <DialogActions>
        {loading > 0 && <CircularProgress
          size={30}
          sx={{
            'position': 'absolute',
            'left': 25,
            'bottom': 12
          }} />}
        <Button color="inherit"
          onClick={() => close()}
          disabled={isSubmitting}>
          CANCELAR
        </Button>
        {usuario?.id && authUser?.roleId === 'superAdmin' && (
          <Button
            color="secondary"
            onClick={() => setConfirmarReenvio(true)}
            disabled={isDirty || isSubmitting || loading > 0}
            title={isDirty ? 'Salve as alterações antes de reenviar o e-mail' : ''}
          >
            REENVIAR BOAS-VINDAS
          </Button>
        )}
        <Button color="primary"
          onClick={handleSubmit(onSave)}
          disabled={!isDirty || isSubmitting}>
          SALVAR
        </Button>
      </DialogActions>
      <DialogoConfirmacaoSimples
        open={confirmarReenvio}
        title="Reenviar e-mail de boas-vindas?"
        onClose={() => setConfirmarReenvio(false)}
        onNao={() => setConfirmarReenvio(false)}
        onSim={reenviarBoasVindas}
      >
        <DialogContentText>
          Um novo link para definição de senha será enviado para <b>{usuario?.email}</b>.
          O link anterior deixará de funcionar.
        </DialogContentText>
      </DialogoConfirmacaoSimples>
    </Dialog>

  );
};


export default DialogoUsuario;
